/**
 * usePresenceHeartbeat - Sends periodic presence updates (DRR-061)
 *
 * Sends ONLINE heartbeat every 60s via updatePresence mutation.
 * Sends AWAY when tab becomes hidden (visibilitychange).
 * Ephemeral: Valkey-only with 90s TTL, no DynamoDB writes.
 *
 * CONSTRAINTS:
 * - Uses gql + useMutation<Record<string, any>>(codegen pending for new mutation)
 * - No-op when not authenticated
 */

import { useEffect, useRef, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- New mutation not yet in codegen
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

const UPDATE_PRESENCE = gql`
  mutation UpdatePresence($status: GqlPresenceStatus!) {
    updatePresence(status: $status)
  }
`;

const HEARTBEAT_INTERVAL_MS = 60_000; // 60 seconds

interface UsePresenceHeartbeatOptions {
  /** Skip heartbeat entirely (e.g. when not authenticated) */
  skip?: boolean;
}

/**
 * Sends periodic presence heartbeats to the backend.
 *
 * Mount once at the layout level. Handles visibility changes automatically.
 */
export function usePresenceHeartbeat({ skip = false }: UsePresenceHeartbeatOptions = {}) {
  const [updatePresence] = useMutation<Record<string, any>>(UPDATE_PRESENCE);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sendPresence = useCallback(
    (status: 'ONLINE' | 'AWAY') => {
      if (skip) return;
      updatePresence({ variables: { status } }).catch(() => {
        // Presence is ephemeral — failures are non-critical
      });
    },
    [skip, updatePresence]
  );

  useEffect(() => {
    if (skip) return;

    // Send initial ONLINE
    sendPresence('ONLINE');

    // Periodic heartbeat
    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        sendPresence('ONLINE');
      }
    }, HEARTBEAT_INTERVAL_MS);

    // Visibility change handler
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        sendPresence('AWAY');
      } else {
        sendPresence('ONLINE');
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [skip, sendPresence]);
}
